import React from 'react';
import { fetchRun, isRunTerminal } from '@services/fmdc';
import { useFmdcPoll } from './useFmdcPoll';

// One run, kept fresh while it is still moving. Loads on mount (and when the id changes), then
// polls GET /runs/:id every 5 s until the run is terminal — review, loadable, error, or stuck. A
// transient error keeps the run already on screen so polling continues rather than blanking the
// page (the backfill history rule).
//
// `setRun` lets a write that returns the run (a rerun's 202) land it without a second fetch.
export const useRun = (runId) => {
  const [run, setRun] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const reload = React.useCallback(
    async (guard) => {
      const result = await fetchRun(runId);
      if (guard?.isCanceled) return result;
      if (result.error) {
        setError(result.error);
      } else {
        setError(null);
        setRun(result.run);
      }
      setIsLoading(false);
      return result;
    },
    [runId]
  );

  React.useEffect(() => {
    const guard = { isCanceled: false };
    setIsLoading(true);
    setRun(null);
    setError(null);
    reload(guard);
    return () => {
      guard.isCanceled = true;
    };
  }, [reload]);

  useFmdcPoll(reload, { enabled: !!run && !isRunTerminal(run) });

  return { run, isLoading, error, reload, setRun };
};
